import { TransportBase, type Transport } from './types.ts';

// Web Bluetooth reaches BLE adapters only - OBDLink CX, and the BLE side of
// dual-mode units. A Bluetooth Classic adapter will never appear here no
// matter how it is paired; that one goes through Web Serial instead.
//
// OBDLink CX exposes service FFF0 with FFF1 for writes and notifications on
// FFF0. Other vendors use the Nordic UART profile, so both are advertised and
// the characteristics are discovered rather than assumed.

const OBDLINK_SERVICE = '0000fff0-0000-1000-8000-00805f9b34fb';
// The HM-10 serial profile, which most inexpensive BLE ELM327 clones use. Its
// FFE1 characteristic is usually a single endpoint carrying both write and
// notify rather than the separate pair FFF0 devices expose - the
// characteristic search below already handles that, since it picks by property
// rather than assuming two.
const HM10_SERIAL_SERVICE = '0000ffe0-0000-1000-8000-00805f9b34fb';
const NORDIC_UART_SERVICE = '6e400001-b5a3-f393-e0a9-e50e24dcca9e';

const CANDIDATE_SERVICES = [OBDLINK_SERVICE, HM10_SERIAL_SERVICE, NORDIC_UART_SERVICE];

/**
 * Names BLE OBD adapters advertise themselves under.
 *
 * Matching is case-sensitive and prefix-only, so "OBD" catches OBDII, OBDLink
 * and OBD-II alike. If an adapter does not appear in the chooser, its name
 * belongs on this list - that is the cost of not showing every device in
 * range.
 */
const OBD_NAME_PREFIXES = [
  'OBD', // OBDII, OBDLink, OBD-II, OBDCheck
  'obd',
  'ELM', // ELM327 clones
  'Vgate',
  'vLinker',
  'Veepeak',
  'iCar', // Vgate iCar series
  'LELink',
  'Konnwei',
  'V-LINK',
  'BLE', // some clones announce as BLE-OBD or similar
];

// BLE writes are capped by MTU. 20 bytes is the safe floor when the negotiated
// MTU is unknown, and OBD commands are short enough that this never hurts.
const CHUNK_SIZE = 20;

/**
 * The device the user last picked, kept for the life of the page.
 *
 * getDevices() would be the proper way to recover a granted device, but it is
 * behind a flag and absent from stock Chrome, so grantedDevices() returns
 * nothing and every connect falls back to the chooser. Holding the object
 * requestDevice() handed us needs no flag: the permission lives on that object,
 * so gatt.connect() reattaches without a prompt.
 *
 * A page reload loses it and the chooser comes back, which is the correct
 * trade rather than a limitation to work around.
 */
let lastGrantedDevice: BluetoothDevice | null = null;

/**
 * Forget the device this session picked, so the next connect asks again.
 *
 * An adapter here is a pathway, not a dongle: "Bluetooth" is a way of reaching
 * a car, and which reader is on the bench today is a separate question. The
 * cache above exists because a granted device cannot be re-requested silently,
 * and it is right for the common case of scanning several cars with one reader.
 *
 * It is wrong the moment somebody owns two readers. Without this they would
 * have to reload the page to use the other one, which is not something anybody
 * would guess.
 */
let showEveryDevice = false;

/**
 * Show every BLE device on the next connection attempt.
 *
 * Cleared once used, so the readable filtered list comes back by default rather
 * than leaving somebody scrolling past their neighbours' headphones forever.
 */
export function showAllDevicesOnce(): void {
  showEveryDevice = true;
  lastGrantedDevice = null;
}

/**
 * Ask for a device now, while the click that triggered this is still live.
 *
 * Chrome only allows requestDevice() during transient user activation, and the
 * scan path awaits several things - a status check, an adapter probe - before
 * it reaches the transport. By then the activation has expired and the call
 * fails with "Must be handling a user gesture", which reads like a Bluetooth
 * fault and is really a timing one.
 *
 * So the chooser is opened from the click handler, first thing, and the device
 * is waiting in the cache by the time the transport wants it. A no-op when one
 * has already been granted this session.
 */
export async function ensureDeviceGranted(): Promise<void> {
  if (lastGrantedDevice) return;
  lastGrantedDevice = await BluetoothTransport.requestDevice();
}

export function forgetGrantedDevice(): void {
  lastGrantedDevice = null;
}

/** The device in use this session, for showing what is actually connected. */
export function grantedDeviceName(): string | null {
  return lastGrantedDevice?.name ?? null;
}

export class BluetoothTransport extends TransportBase implements Transport {
  readonly kind = 'ble' as const;
  label = 'Bluetooth LE';

  private device: BluetoothDevice | null;
  private server: BluetoothRemoteGATTServer | null = null;
  private writeChar: BluetoothRemoteGATTCharacteristic | null = null;
  private notifyChar: BluetoothRemoteGATTCharacteristic | null = null;
  private onDisconnect = () => this.emitStatus('closed', 'Adapter disconnected.');

  constructor(device?: BluetoothDevice) {
    super();
    this.device = device ?? null;
    if (device?.name) this.label = `BLE — ${device.name}`;
  }

  get isOpen() {
    return Boolean(this.server?.connected && this.writeChar);
  }

  /**
   * Known readers by default, everything on request.
   *
   * Both extremes are wrong. Filtering on a name-prefix list hid a real adapter
   * that announced itself as NF79L, with nothing on screen to explain why it
   * was missing. Showing every radio in range instead buries the reader among
   * watches, headphones and whatever else is in the building.
   *
   * So the filter stays, and `acceptAll` is the way past it - offered in the UI
   * rather than hidden, because the list can only ever cover the adapters
   * somebody thought of.
   */
  static async requestDevice(acceptAll = false): Promise<BluetoothDevice> {
    if (!('bluetooth' in navigator)) {
      throw new Error('Web Bluetooth is unavailable in this browser.');
    }
    // Narrow the chooser by name as well as by service.
    //
    // Filtering on advertised service UUIDs alone is what hid these adapters
    // in the first place: most advertise a name and nothing else, exposing
    // their serial service only once connected. Accepting every device fixed
    // that but filled the list with headphones and watches.
    //
    // Filters are OR'd, so a device shows if it advertises a known service or
    // carries a known name. The prefixes cover the adapters people actually
    // buy; anything else needs adding here, which is a deliberate trade
    // against showing every radio in range.
    if (acceptAll) {
      return navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: CANDIDATE_SERVICES,
      });
    }

    return navigator.bluetooth.requestDevice({
      filters: [
        ...CANDIDATE_SERVICES.map((s) => ({ services: [s] })),
        ...OBD_NAME_PREFIXES.map((namePrefix) => ({ namePrefix })),
      ],
      optionalServices: CANDIDATE_SERVICES,
    });
  }

  /** Devices already permitted, so a return visit can reconnect silently. */
  static async grantedDevices(): Promise<BluetoothDevice[]> {
    if (!('bluetooth' in navigator) || !navigator.bluetooth.getDevices) return [];
    try {
      return await navigator.bluetooth.getDevices();
    } catch {
      return [];
    }
  }

  async open() {
    this.emitStatus('connecting', 'Pairing over BLE');

    // Prefer a device this origin has already been granted. The chooser needs
    // the adapter to be advertising, and a peripheral stops advertising the
    // moment something connects to it, so a prompt is the worst way to get
    // back to a device we are already allowed to use.
    //
    // Only when exactly one has been granted: with several, picking for the
    // user would silently connect to the wrong adapter.
    if (!this.device) {
      // The device from earlier in this session, if there was one.
      this.device = lastGrantedDevice;

      // Then getDevices(), for browsers that have it - it survives a reload,
      // which the cached object does not.
      if (!this.device) {
        const granted = await BluetoothTransport.grantedDevices();
        if (granted.length === 1) this.device = granted[0];
      }
    }

    if (!this.device) {
      this.device = await BluetoothTransport.requestDevice(showEveryDevice);
      showEveryDevice = false;
    }
    lastGrantedDevice = this.device;
    if (this.device.name) this.label = `BLE — ${this.device.name}`;

    this.device.addEventListener('gattserverdisconnected', this.onDisconnect);

    let server: BluetoothRemoteGATTServer | undefined;
    try {
      server = await this.device.gatt?.connect();
    } catch (err) {
      // A remembered device that is off, out of range or already claimed by
      // something else. Fall back to the chooser rather than dead-ending on a
      // stale grant.
      this.device.removeEventListener('gattserverdisconnected', this.onDisconnect);
      this.device = await BluetoothTransport.requestDevice();
      lastGrantedDevice = this.device;
      this.device.addEventListener('gattserverdisconnected', this.onDisconnect);
      server = await this.device.gatt?.connect();
      if (!server) throw err;
    }
    if (!server) throw new Error('GATT server refused the connection.');
    this.server = server;

    const service = await firstAvailableService(server);
    if (!service) {
      // Report what the device actually offers. Web Bluetooth only returns
      // services named in optionalServices, so this is the allowed subset - but
      // it is the difference between "add this UUID" and "this is not a serial
      // device at all", which is the whole question at this point.
      let found = '';
      try {
        const services = await server.getPrimaryServices();
        found = services.map((x) => x.uuid).join(', ');
      } catch {
        found = '';
      }
      throw new Error(
        `No usable serial service on this device. It exposes: ${found || 'nothing readable'}. ` +
          'If a UUID is listed there, it needs adding to CANDIDATE_SERVICES; if the list is empty, ' +
          'the adapter may be Bluetooth Classic and belongs on the Web Serial transport.',
      );
    }

    const chars = await service.getCharacteristics();
    this.writeChar =
      chars.find((c) => c.properties.writeWithoutResponse) ?? chars.find((c) => c.properties.write) ?? null;
    this.notifyChar = chars.find((c) => c.properties.notify) ?? null;

    if (!this.writeChar || !this.notifyChar) {
      throw new Error('Found the service but not a usable write/notify characteristic pair.');
    }

    this.notifyChar.addEventListener('characteristicvaluechanged', (ev) => {
      const value = (ev.target as BluetoothRemoteGATTCharacteristic).value;
      if (value) this.emitData(new Uint8Array(value.buffer));
    });
    await this.notifyChar.startNotifications();

    this.emitStatus('open', this.label);
  }

  async close() {
    this.emitStatus('closing');
    this.device?.removeEventListener('gattserverdisconnected', this.onDisconnect);
    try {
      await this.notifyChar?.stopNotifications();
    } catch {
      // notifications already stopped
    }
    try {
      this.server?.disconnect();
    } catch {
      // already disconnected
    }
    this.writeChar = null;
    this.notifyChar = null;
    this.server = null;
    this.emitStatus('closed');
  }

  async write(bytes: Uint8Array) {
    const char = this.writeChar;
    if (!char) throw new Error('Bluetooth transport is not open.');
    for (let i = 0; i < bytes.length; i += CHUNK_SIZE) {
      const chunk = bytes.slice(i, i + CHUNK_SIZE);
      if (char.properties.writeWithoutResponse) {
        await char.writeValueWithoutResponse(chunk);
      } else {
        await char.writeValueWithResponse(chunk);
      }
    }
  }
}

async function firstAvailableService(server: BluetoothRemoteGATTServer) {
  for (const uuid of CANDIDATE_SERVICES) {
    try {
      return await server.getPrimaryService(uuid);
    } catch {
      // not this one; try the next
    }
  }
  return null;
}
